import React, { useEffect, useState } from 'react';
import axios from 'axios';
import api from '../../api'
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlus, FiX, FiCheck, FiSettings, FiLoader, FiXCircle, FiDroplet, FiEdit, FiMenu } from 'react-icons/fi';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import progress2 from '../../image/progress2.gif';

const API_URL = '/parametrages/etapes';

// Badge couleur dans le tableau
const ColorBadge = ({ color }) => (
  <div className="flex items-center justify-center">
    <div
      className="w-6 h-6 rounded-full border border-gray-300 shadow-sm"
      style={{ backgroundColor: color }}
      title={color}
    />
  </div>
);

const ParametraEtapesAutoCss = () => {
  const theme = {
    colors: {
      primary: '#2C3E50', accent: '#875A7B', light: '#ECF0F1', danger: '#E74C3C', border: '#BDC3C7', text: '#2C3E50', textSecondary: '#7F8C8D'
    },
    styles: {
      button: 'flex items-center px-4 py-2 rounded-md shadow-sm transition-colors duration-200 font-medium',
      input: 'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500',
      tableHeader: 'bg-gray-800 text-white',
      tableRowEven: 'bg-white',
      tableRowOdd: 'bg-gray-50'
    }
  };

  const [etapes, setEtapes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ nom: '', order: '', couleur: theme.colors.accent });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editNom, setEditNom] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Vérifier la taille de l'écran
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch list
  const fetchEtapes = async () => {
    setLoading(true);
    try {
      const res = await api.get(API_URL);
      setEtapes(res.data.etapes.sort((a, b) => a.order - b.order));
    } catch {
      toast.error("Erreur lors du chargement des étapes");
      setError("Erreur lors du chargement des étapes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEtapes(); }, []);

  // Submit new
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(false);
    try {
      await api.post(API_URL, formData);
      toast.success('Étape ajoutée avec succès');
      fetchEtapes();
      setShowForm(false);
      setFormData({ nom: '', order: '', couleur: theme.colors.accent });
    } catch {
      toast.error("Erreur lors de l'enregistrement");
      setError("Erreur lors de l'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  // Update one etape (name/order)
  const updateEtape = async (id, updatedData) => {
    try {
      await api.post(`${API_URL}/${id}`, updatedData);
    } catch (err) {
      console.error('Erreur update:', err);
      toast.error("Erreur lors de la mise à jour");
      setError("Erreur lors de la mise à jour");
    }
  };

  // On drag end
  const onDragEnd = ({ destination, source }) => {
    if (!destination) return;
    const items = Array.from(etapes);
    const [moved] = items.splice(source.index, 1);
    items.splice(destination.index, 0, moved);
    const reordered = items.map((etape, idx) => ({ ...etape, order: idx + 1 }));
    setEtapes(reordered);
    reordered.forEach(e => {
      if (etapes.find(old => old.id === e.id).order !== e.order) {
        updateEtape(e.id, { nom: e.nom, order: e.order, couleur: e.couleur });
      }
    });
  };

  // Inline edit name
  const handleNameClick = (etape) => {
    setEditingId(etape.id);
    setEditNom(etape.nom);
  };

  const handleNameBlur = (etape) => {
    setEditingId(null);
    if (editNom.trim() && editNom !== etape.nom) {
      const updated = { nom: editNom, order: etape.order, couleur: etape.couleur };
      setEtapes(prev => prev.map(e => e.id === etape.id ? { ...e, nom: editNom } : e));
      updateEtape(etape.id, updated);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <img src={progress2} alt="Loading" className="w-24 h-24" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 md:mb-8 gap-4">
          <h1 className="text-xl md:text-2xl font-bold text-terrecuite-900 flex items-center">
            <FiSettings className="mr-2 md:mr-3 text-terrecuite-700" /> Paramétrage des Étapes
          </h1>
          <button 
            onClick={() => setShowForm(true)} 
            className="flex items-center px-3 py-2 md:px-4 md:py-2 bg-terrecuite-600 text-white rounded-md hover:bg-terrecuite-700 transition-colors duration-200 w-full md:w-auto justify-center"
          >
            <FiPlus className="mr-2" /> Ajouter une étape
          </button>
        </div>
  
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <FiLoader className="animate-spin text-4xl text-terrecuite-600" />
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-lg border border-terrecuite-200 overflow-x-auto">
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="etapes-list">
                {(provided) => (
                  <table
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="min-w-full divide-y divide-terrecuite-200"
                  >
                    <thead className="bg-terrecuite-600 text-white">
                      <tr>
                        <th className="px-4 py-3 md:px-6 md:py-3 text-left text-xs font-medium uppercase tracking-wider">Nom</th>
                        {!isMobile && (
                          <th className="px-4 py-3 md:px-6 md:py-3 text-center text-xs font-medium uppercase tracking-wider">Ordre</th>
                        )}
                        <th className="px-4 py-3 md:px-6 md:py-3 text-center text-xs font-medium uppercase tracking-wider">Couleur</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-terrecuite-100">
                      {etapes.map((etape, index) => (
                        <Draggable key={etape.id} draggableId={`${etape.id}`} index={index}>
                          {(provided) => (
                            <tr
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`hover:bg-terrecuite-50 transition-colors duration-150 ${
                                index % 2 === 0 ? 'bg-white' : 'bg-terrecuite-50'
                              }`}
                            >
                              <td className="px-4 py-4 md:px-6 md:py-4 whitespace-nowrap text-sm text-terrecuite-900">
                                <div className="flex flex-col md:flex-row md:items-center">
                                  {isMobile && (
                                    <span className="text-xs text-terrecuite-500 mb-1 font-medium">#{index + 1}</span>
                                  )}
                                  {editingId === etape.id ? (
                                    <input
                                      autoFocus
                                      className="border border-terrecuite-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-terrecuite-500 focus:border-transparent w-full"
                                      value={editNom}
                                      onChange={(e) => setEditNom(e.target.value)}
                                      onBlur={() => handleNameBlur(etape)}
                                    />
                                  ) : (
                                    <span
                                      onClick={() => handleNameClick(etape)}
                                      className="cursor-pointer flex items-center hover:text-terrecuite-700 transition-colors duration-150"
                                    >
                                      <FiEdit className="mr-1 text-terrecuite-400 hidden md:block" /> 
                                      {isMobile && etape.nom.length > 20 ? `${etape.nom.substring(0, 20)}...` : etape.nom}
                                      {!isMobile && etape.nom}
                                    </span>
                                  )}
                                </div>
                              </td>
                              {!isMobile && (
                                <td className="px-4 py-4 md:px-6 md:py-4 whitespace-nowrap text-sm text-terrecuite-700 text-center font-medium">
                                  {index + 1}
                                </td>
                              )}
                              <td className="px-4 py-4 md:px-6 md:py-4 whitespace-nowrap text-sm text-terrecuite-700 text-center">
                                <ColorBadge color={etape.couleur} />
                              </td>
                            </tr>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </tbody>
                  </table>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        )}
  
        {/* Modal Formulaire */}
        <AnimatePresence>
          {showForm && (
            <motion.div
              className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
                className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 border border-terrecuite-200 p-4 md:p-6 relative"
              >
                <button
                  onClick={() => setShowForm(false)}
                  className="absolute top-3 right-3 md:top-4 md:right-4 text-terrecuite-500 hover:text-terrecuite-700 transition-colors duration-150"
                >
                  <FiX size={20} />
                </button>
                <h2 className="text-lg md:text-xl font-semibold mb-4 text-terrecuite-900">
                  Ajouter une nouvelle étape
                </h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1 text-terrecuite-800">Nom</label>
                    <input
                      type="text"
                      required
                      className="w-full border border-terrecuite-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-terrecuite-500 focus:border-transparent"
                      value={formData.nom}
                      onChange={(e) =>
                        setFormData({ ...formData, nom: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-terrecuite-800">Ordre</label>
                    <input
                      type="number"
                      required
                      min="1"
                      className="w-full border border-terrecuite-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-terrecuite-500 focus:border-transparent"
                      value={formData.order}
                      onChange={(e) =>
                        setFormData({ ...formData, order: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-terrecuite-800">Couleur</label>
                    <div className="flex items-center gap-4">
                      <input
                        type="color"
                        value={formData.couleur}
                        onChange={(e) =>
                          setFormData({ ...formData, couleur: e.target.value })
                        }
                        className="h-10 w-10 rounded-full border border-terrecuite-300 shadow-sm cursor-pointer hover:border-terrecuite-500 transition-colors duration-150"
                      />
                      <span className="text-sm text-terrecuite-700 flex items-center">
                        <FiDroplet className="mr-1" /> {formData.couleur}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col-reverse md:flex-row justify-end gap-3 mt-6">
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className="px-4 py-2 border border-terrecuite-300 text-terrecuite-700 rounded-md hover:bg-terrecuite-100 transition-colors duration-150 mt-2 md:mt-0"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-terrecuite-600 text-white rounded-md hover:bg-terrecuite-700 transition-colors duration-150 flex items-center justify-center"
                    >
                      <FiCheck className="mr-2" /> Enregistrer
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
} 

export default ParametraEtapesAutoCss;